// Story 8.10 (ADO-2019), AC1 / AC2 / DV-39: the HHT orders details in a Zustand store.
//
// One read, `GET /api/v1/hht-orders` (each waiting order classified at read time, with its
// Ready lines and cases, its attention lines and the depots it adds to; and the history),
// feeds the /choose card's summary line and the /hht-orders pages. The header badge and the
// card's title and button read the cheap count instead (`useHhtWaitingCountStore`, DV-39),
// so a failure here never hides them. It is read on demand: on entering /choose or the HHT
// pages, and after orders are accepted (`markAccepted`, every order the accept named, story
// DV-18), which drops them from `waiting` at once and re-reads FRESH: a read that started
// before the accept still lists them, so it is neither joined nor allowed to land.
//
// A failed read degrades silently: the store keeps what it last had (nothing, before the
// first success). The pages read `status` to offer a retry; the card reads `loaded` to show
// its summary line once the details have arrived and to leave it out when they never do.

import { create } from 'zustand';
import { getHhtOrders, type HhtHistoryOrder, type HhtWaitingOrder } from '../api/hht-orders.client';

export type HhtOrdersStatus = 'idle' | 'loading' | 'ready' | 'error';

interface HhtOrdersStoreState {
  readonly status: HhtOrdersStatus;
  /** Whether a read has succeeded this session, so `waiting` and `history` are real. */
  readonly loaded: boolean;
  /** Whether the HHT workflow applies to the caller (server-decided, story D-8). */
  readonly applies: boolean;
  /** Waiting orders, oldest first. */
  readonly waiting: readonly HhtWaitingOrder[];
  /** Accepted orders, newest first (at most the latest 20). */
  readonly history: readonly HhtHistoryOrder[];
  /** How many accepted orders the Customer has in all (the history pill). */
  readonly historyTotal: number;
  /** (Re)read the overview. Concurrent calls share the request in flight. Never rejects. */
  load: () => Promise<void>;
  /**
   * Orders were accepted together (DV-18): drop them from `waiting` now, and
   * re-read the overview with a NEW request, discarding any read already in flight
   * (it started before the accept). Never rejects.
   */
  markAccepted: (hhtOrderIds: readonly string[]) => Promise<void>;
  /** Session hygiene: drop everything (sign-out, account switch). */
  reset: () => void;
}

const EMPTY = {
  status: 'idle' as const,
  loaded: false,
  applies: false,
  waiting: [] as readonly HhtWaitingOrder[],
  history: [] as readonly HhtHistoryOrder[],
  historyTotal: 0,
};

// The request in flight, so a second caller joins it, and a generation bumped by
// `reset` (a sign-out) and `markAccepted` (an accept) so a read started before either
// cannot land afterwards: not the prior user's data, not an order that was just accepted.
let inFlight: Promise<void> | null = null;
let generation = 0;

export const useHhtOrdersStore = create<HhtOrdersStoreState>((set, get) => ({
  ...EMPTY,

  markAccepted(hhtOrderIds) {
    generation += 1;
    inFlight = null;
    const accepted = new Set(hhtOrderIds);
    set((state) => ({
      waiting: state.waiting.filter((order) => !accepted.has(order.hhtOrderId)),
    }));
    return get().load();
  },

  load() {
    if (inFlight !== null) return inFlight;
    const started = generation;
    set({ status: 'loading' });
    const run: Promise<void> = (async () => {
      try {
        const overview = await getHhtOrders();
        if (started !== generation) return;
        set({
          status: 'ready',
          loaded: true,
          applies: overview.applies,
          waiting: overview.waiting,
          history: overview.history,
          historyTotal: overview.historyTotal,
        });
      } catch {
        if (started !== generation) return;
        set({ status: 'error' });
      }
    })();
    inFlight = run;
    void run.finally(() => {
      if (inFlight === run) inFlight = null;
    });
    return run;
  },

  reset() {
    generation += 1;
    inFlight = null;
    set({ ...EMPTY });
  },
}));
